import React, { useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import {
    Text,
    ActionButton,
    TooltipHost,
    TooltipDelay,
    DirectionalHint,
    ITooltipProps,
    ITooltipHostStyles,
    Modal,
    TextField,
    DatePicker,
    Link,
    IContextualMenuProps
} from '@fluentui/react';
import {
    BugInformations,
    DeviceModelDto,
    EnumLookUp,
    FieldTypeDetail,
    HashTagDto,
    RequestDto,
    RequestType,
    RequestTypeDetailsDto
} from 'generated/request-backend';
import RequestTypeDropdown from '../requestTypeDropdown/RequestTypeDropdown';
import AgingProgress from 'app/components/agingProgress/AgingProgress';
import {
    ISOToDateTime,
    GetText,
    GetTypes,
    filterField,
    formatCurrency,
    highlightTags,
    isValidURL,
    ReplaceUrlByAnchor,
    injectTargetToAnchor
} from 'app/components/helpers';

import ActionDropdown from '../actionDropdown/ActionDropdown';
import './RequestDetailsTab.scss';
import { useBoolean, useId } from '@fluentui/react-hooks';

import * as _ from 'lodash';
import AddProduct from '../addProduct/AddProduct';
import { useQueryAllDeviceList } from 'app/services/request-api';
import NumberFormat, { NumberFormatValues } from 'react-number-format';
import BugInformationModal from '../bugInformationModal/BugInformationModal';
import { BlobStorageContainer, Languages, StringConstants } from 'app/utils/constants';
import { findDeviceIconUrl, getDeviceIcon } from 'app/utils/SVGIcons';
import { BugInformation } from 'app/enums/BugInformation.enum';
import DOMPurify from 'dompurify';
import { NO_CUSTOMER_PLACEHOLDER } from 'app/modules/newTicketView/customerDisplay';
import { isGeneralFeedbackTicket } from 'app/modules/newTicketView/generalFeedback';
import { ViewerRole } from 'app/modules/newTicketView/types';
import ContextualDetailsCard from './ContextualDetailsCard';
import FeedbackSourceCard from './FeedbackSourceCard';
import GeneralFeedbackEmptyState from './GeneralFeedbackEmptyState';
import { getRequestImpactTotals } from './requestImpactTotals';
import DetailCard from './DetailCard';
import RequestRichTextEditor from '../requestRichTextEditor/RequestRichTextEditor';
import moment from 'moment';

interface IProps {
    selectedRequest: RequestDto;
    requestDetailsLookup?: RequestTypeDetailsDto[];
    isEditMode?: boolean;
    resolutionDate?: Date;
    setResolutionDate?: (resolutionDate: Date) => void;
    statusMenu?: IContextualMenuProps;
    statusTypes?: EnumLookUp[];
    statusSelection?: number;
    onCompleteHandler?: (fieldName: string, fieldValue: number | boolean) => void;
    whatbusinessproblemissolved?: string;
    setwhatbusinessproblemissolved?: (whatBusinessProblemIsSolved: string) => void;
    competitiveoffering?: string;
    setCompetitiveoffering?: (competitiveOffering: string) => void;
    isdesignforsurfacedfspossible?: string;
    setIsdesignforsurfacedfspossible?: (isDesignForSurfaceDFSpossible: string) => void;
    dcfJustification?: string;
    setDcfJustification?: (dcfJustification: string) => void;
    dcfApprovedValue?: number;
    setDcfApprovedValue?: (dcfApprovedValue: number) => void;
    dcfSR?: string;
    setDcfSR?: (dcfSR: string) => void;
    cssCaseNumber?: string;
    setCssCaseNumber?: (cssCaseNumber: string) => void;
    answer?: string;
    setAnswer?: (answer: string) => void;
    whyThisDateIsImportant?: string;
    setWhyThisDateIsImportant?: (whyThisDateIsImportant: string) => void;
    setTextToSubmit?: (textToSubmit: string) => void;
    handleAddProduct: Function;
    hideLoadingScreen: boolean;
    bugInfoUpdateHandler?: (bugInformations: BugInformations[]) => void;
    hashTagsFormatted: HashTagDto[];
    setHashTagsFormatted: (hashTagsFormatted: HashTagDto[]) => void;
    isNewTicketViewEnabled?: boolean;
}

const hostStyles: Partial<ITooltipHostStyles> = {
    root: { display: 'inline-block', borderRadius: '13px' }
};

const RequestDetailsTab: React.FC<IProps> = (props) => {
    const {
        selectedRequest,
        isEditMode,
        resolutionDate,
        statusMenu,
        statusTypes,
        statusSelection,
        whatbusinessproblemissolved,
        competitiveoffering,
        isdesignforsurfacedfspossible,
        dcfJustification,
        dcfApprovedValue,
        dcfSR,
        cssCaseNumber,
        answer,
        requestDetailsLookup,
        whyThisDateIsImportant,
        hashTagsFormatted,
        isNewTicketViewEnabled = false
    } = props;
    const {
        setResolutionDate,
        onCompleteHandler,
        setwhatbusinessproblemissolved,
        setCompetitiveoffering,
        setIsdesignforsurfacedfspossible,
        setDcfJustification,
        setDcfApprovedValue,
        setDcfSR,
        setCssCaseNumber,
        setAnswer,
        setTextToSubmit,
        handleAddProduct,
        bugInfoUpdateHandler,
        setHashTagsFormatted
    } = props;
    const tooltipId = useId('tooltip');
    // General Feedback tickets have no customer, so MSX aggregates render as a placeholder.
    const isGeneralFeedback = isNewTicketViewEnabled && isGeneralFeedbackTicket(selectedRequest);
    const impactTotals = getRequestImpactTotals(selectedRequest);
    const [isModalOpen, { setTrue: showModal, setFalse: hideModal }] = useBoolean(false);
    const [isBugInfoModalOpen, setIsBugInfoModalOpen] = useState(false);
    const yesNoLookup = [
        {
            id: true,
            name: StringConstants.YES
        },
        {
            id: false,
            name: 'No'
        }
    ];

    //handle editable fields
    const [isEdittingRequestDetails, setIsEdittingRequestDetails] = useState<boolean>(false);
    const [businessProblemFieldValue, setBusinessProblemFieldValue] = useState<string>('');
    const [compOfferFieldValue, setCompOfferFieldValue] = useState<string>('');
    const [isDfsFieldValue, setIsDfsFieldValue] = useState<string>('');
    const [cssCaseNumberFieldValue, setCssCaseNumberFieldValue] = useState<string>('');
    const [dcfJustificationFieldValue, setDcfJustificationFieldValue] = useState<string>('');
    const [dcfApprovedFieldValue, setDcfApprovedFieldValue] = useState<number>(null);
    const [dcfSRFieldValue, setDcfSRFieldValue] = useState<string>('');
    const [answerFieldValue, setAnswerFieldValue] = useState<string>('');
    const [resolutionDateFieldValue, setResolutionDateFieldValue] = useState<Date>();

    useEffect(() => {
        setBusinessProblemFieldValue(whatbusinessproblemissolved);
        setCompOfferFieldValue(competitiveoffering);
        setIsDfsFieldValue(isdesignforsurfacedfspossible);
        setCssCaseNumberFieldValue(cssCaseNumber);
        setDcfApprovedFieldValue(dcfApprovedValue);
        setDcfJustificationFieldValue(dcfJustification);
        setDcfSRFieldValue(dcfSR);
        setAnswerFieldValue(answer);
        setResolutionDateFieldValue(resolutionDate);
    }, [
        answer,
        competitiveoffering,
        cssCaseNumber,
        dcfApprovedValue,
        dcfJustification,
        dcfSR,
        isdesignforsurfacedfspossible,
        isEditMode,
        resolutionDate,
        whatbusinessproblemissolved,
    ]);

    // handler functions
    const getFieldValues = (fieldName: string) => {
        const teamDetails: RequestTypeDetailsDto = _.find(requestDetailsLookup, { requestTeamId: selectedRequest.requestTeam });
        if (teamDetails) {
            const requestTypeDetails: FieldTypeDetail = _.find(teamDetails.fieldTypeDetail, {
                requestTypeId: selectedRequest.requestType
            });
            if (requestTypeDetails) {
                return requestTypeDetails[fieldName as keyof FieldTypeDetail];
            }
        }
    };
    const getNameByID = (val: {id?: number | boolean, name?: string}[], id?: number | boolean) => {
        const res = val.filter((item) => item.id === id)[0]?.name;
        if (!res) return '--';
        return res;
    };
    const { isLoading: isDeviceListLoaded, data: getDeviceList } = useQueryAllDeviceList();

    const getDeviceIconSrc = (device: DeviceModelDto) => {
        let iconUrl: string = undefined;
        if (selectedRequest) {
            iconUrl = findDeviceIconUrl(getDeviceList, device);
        }
        return iconUrl ? getDeviceIcon(iconUrl) : getDeviceIcon();
    };

    const tooltipProps: ITooltipProps = React.useMemo(
        () => ({
            onRenderContent: () => (
                <ul className="device-list">
                    {!isDeviceListLoaded &&
                        selectedRequest &&
                        selectedRequest.deviceModels &&
                        selectedRequest.deviceModels?.slice(1).map((device) => {
                            return (
                                <li>
                                    <img src={getDeviceIconSrc(device)} alt={device.name} title={device.name} />
                                    <span className="device-txt">
                                        {device.name} ({device.deviceCount})
                                    </span>
                                </li>
                            );
                        })}
                </ul>
            ),

            calloutProps: {
                styles: {
                    beak: { padding: '0' },
                    beakCurtain: { borderRadius: '13px', padding: '0' },
                    calloutMain: { borderRadius: '10px' }
                }
            }
        }),
        [selectedRequest, isDeviceListLoaded]
    );

    const handleResolutionDate = (newValue: Date) => {
        setIsEdittingRequestDetails(true);
        setResolutionDate(newValue);
        setResolutionDateFieldValue(newValue);
    };

    const handleWhatbusinessproblemissolvedChange = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) => {
        setIsEdittingRequestDetails(true);
        setBusinessProblemFieldValue(newValue);
        setwhatbusinessproblemissolved(newValue);
    };

    const handleCompetitiveofferingChange = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) => {
        setIsEdittingRequestDetails(true);
        setCompOfferFieldValue(newValue);
        setCompetitiveoffering(newValue);
    };

    const handleIsdesignforsurfacedfspossibleChange = (
        event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) => {
        setIsEdittingRequestDetails(true);
        setIsDfsFieldValue(newValue);
        setIsdesignforsurfacedfspossible(newValue);
    };

    const handleDcfJustificationChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setIsEdittingRequestDetails(true);
        setDcfJustificationFieldValue(newValue);
        setDcfJustification(newValue);
    };

    const handleDcfApprovedValueChange = (value: NumberFormatValues) => {
        setIsEdittingRequestDetails(true);
        setDcfApprovedFieldValue(value.floatValue);
        setDcfApprovedValue(value.floatValue);
    };

    const handleDcfSRChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setIsEdittingRequestDetails(true);
        setDcfSRFieldValue(newValue);
        setDcfSR(newValue);
    };

    const handleCssCaseNumberChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setIsEdittingRequestDetails(true);
        setCssCaseNumberFieldValue(newValue);
        setCssCaseNumber(newValue);
    };

    const handleAnswerChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        setIsEdittingRequestDetails(true);
        setAnswerFieldValue(newValue);
        setAnswer(newValue);
    };

    const convertToURL = (stringValue: string, className: string, openInNewTab: boolean) => {
        if (!stringValue) {
            return '';
        }
        let classValue = className ? className : '';
        let targetValue = openInNewTab ? 'target="_blank"' : '';
        return stringValue.replaceAll(
            // eslint-disable-next-line no-useless-escape
            /(\b(https?|ftp|file):\/\/[\-A-Z0-9+&@#\/%?=~_|!:,.;]*[\-A-Z0-9+&@#\/%=~_|])/gim,
            ` <a class="${classValue}" href="$1" ${targetValue}>$1</a>`
        );
    };

    return (
        <div className="request-details-tab">
            <DetailCard
                title={<Trans>Basic Information</Trans>}
                useCardLayout={isNewTicketViewEnabled}>
            <table className="dp-content-details-tbl">
                <tbody>
                    <tr className="tbl-header">
                        <td>
                            <Trans>REQUEST TYPE</Trans>
                        </td>
                        <td>
                            <Trans>MSX REVENUE</Trans>
                        </td>
                        <td>
                            <Trans>MSX DEVICE</Trans>
                        </td>
                        <td>
                            <Trans>AGING</Trans>
                        </td>
                        <td>
                            <Trans>STATUS</Trans>
                        </td>
                    </tr>
                    <tr>
                        {(selectedRequest.requestTeam === 3 ||
                            selectedRequest.status === 4 ||
                            selectedRequest.status === 5 ||
                            selectedRequest.isHidden) && <td>{GetTypes(selectedRequest.requestType)}</td>}
                        {selectedRequest.requestTeam !== 3 &&
                            !(selectedRequest.status === 4 || selectedRequest.status === 5) &&
                            !selectedRequest.isHidden && (
                                <td>
                                    <RequestTypeDropdown selectedRequest={selectedRequest} />
                                </td>
                            )}
                        {selectedRequest.isParent ? (
                            <>
                                <td>
                                    ${' '}
                                    {new Intl.NumberFormat(Languages.EN_US, { style: 'decimal' }).format(
                                        Math.round(
                                            selectedRequest.msxCurrentTotalRevenue
                                                ? selectedRequest.msxCurrentTotalRevenue
                                                : selectedRequest.msxTotalRevenue
                                        )
                                    )}
                                </td>
                                <td data-testid="admin-basic-info-msx-device">
                                    {isNewTicketViewEnabled
                                        ? isGeneralFeedback
                                            ? NO_CUSTOMER_PLACEHOLDER
                                            : impactTotals.devices
                                        : selectedRequest.msxCurrentTotalDeviceCount
                                            ? selectedRequest.msxCurrentTotalDeviceCount
                                            : selectedRequest.msxTotalDeviceCount}
                                </td>
                            </>
                        ) : (
                            <>
                                <td>
                                    ${' '}
                                    {new Intl.NumberFormat(Languages.EN_US, { style: 'decimal' }).format(
                                        Math.round(
                                            selectedRequest.msxCurrentTotalRevenue
                                                ? selectedRequest.msxCurrentTotalRevenue
                                                : selectedRequest.msxTotalRevenue
                                        )
                                    )}
                                </td>
                                <td data-testid="admin-basic-info-msx-device">
                                    {isNewTicketViewEnabled
                                        ? isGeneralFeedback
                                            ? NO_CUSTOMER_PLACEHOLDER
                                            : impactTotals.devices
                                        : selectedRequest.msxCurrentTotalDeviceCount
                                            ? selectedRequest.msxCurrentTotalDeviceCount
                                            : selectedRequest.msxTotalDeviceCount}
                                </td>
                            </>
                        )}
                        <td>
                            <AgingProgress
                                startDate={selectedRequest?.createdOn}
                                endDate={selectedRequest?.requestedResolutionDate}
                                closedDate={selectedRequest?.closedOn? selectedRequest?.closedOn : selectedRequest?.requestedResolutionDate}
                                status={selectedRequest?.status}
                            />
                        </td>
                        <td>
                            {!isEditMode && !selectedRequest.isHidden && (
                                <ActionButton menuProps={statusMenu} className="status-selection-menu">
                                    {GetText(statusTypes, statusSelection)}
                                </ActionButton>
                            )}
                            {selectedRequest.isHidden && <Text>{GetText(statusTypes, statusSelection)}</Text>}
                            {isEditMode && (
                                <ActionButton menuProps={statusMenu} className="status-selection-menu">
                                    {GetText(statusTypes, statusSelection)}
                                </ActionButton>
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
            </DetailCard>
            <DetailCard
                title={<Trans>Product Details</Trans>}
                useCardLayout={isNewTicketViewEnabled}
                legacyTitle="TROUBLED PRODUCTS:"
                legacyTitleClassName="tab-details-heading">
            <div className="ntv-card-actions">
                {!selectedRequest.isHidden && !selectedRequest.isParent && (
                    <span
                        className="txt-links update-product-link"
                        onClick={() => {
                            showModal();
                        }}>
                        Update products
                    </span>
                )}
            </div>
            {hideModal && (
                <Modal
                    layerProps={{ eventBubblingEnabled: true }}
                    titleAriaId={'Update Products'}
                    isOpen={isModalOpen}
                    onDismiss={hideModal}
                    isBlocking={false}
                    className="add-impact-model">
                    <AddProduct
                        addOrUpdate={StringConstants.UPDATE}
                        selectedRequest={selectedRequest}
                        onDismiss={hideModal}
                        handleAddProduct={() => {
                            handleAddProduct();
                            hideModal();
                        }}
                    />
                </Modal>
            )}

            <div className="tbl-header request-details-row row-flex-box">
                {getFieldValues('productCategory') && (
                    <div className=" request-details-col">
                        {' '}
                        <div className="header-tab">
                            {isNewTicketViewEnabled ? 'Product family' : 'PRODUCT CATEGORY'}
                        </div>{' '}
                        {!selectedRequest.isHidden ? (
                            <div className="content-tab">
                                <ActionDropdown
                                    data={getFieldValues('productCategory') as EnumLookUp[] | {id?: number | boolean, name: string}[]}
                                    onCompleteHandler={onCompleteHandler}
                                    fieldName="productCategory"
                                    currentValue={selectedRequest.productCategory}
                                    isEditMode={isEditMode}
                                    selectedRequest={selectedRequest}
                                />
                            </div>
                        ) : (
                            <div className="mr-top">
                                <Text>{getNameByID(getFieldValues('productCategory') as EnumLookUp[], selectedRequest.productCategory)}</Text>
                            </div>
                        )}
                    </div>
                )}

                <div className="request-details-col ">
                    <div className="header-tab ">
                        {isNewTicketViewEnabled ? 'Product model' : 'PRODUCT ADDED'}
                    </div>{' '}
                    <div className="row-flex-box content-tab align-center">
                        {!isDeviceListLoaded &&
                            selectedRequest &&
                            selectedRequest?.deviceModels &&
                            selectedRequest?.deviceModels.length === 0 &&
                            '--'}
                        {!isDeviceListLoaded &&
                            selectedRequest &&
                            selectedRequest?.deviceModels &&
                            selectedRequest?.deviceModels.length > 0 && (
                                <img
                                    src={ getDeviceIconSrc(selectedRequest.deviceModels[0]) }
                                    alt="Device"
                                    title="Device Image"
                                    className="pro-img"
                                />
                            )}
                        {!isDeviceListLoaded &&
                            selectedRequest &&
                            selectedRequest?.deviceModels &&
                            selectedRequest?.deviceModels.length > 0 && (
                                <Text>
                                    {selectedRequest?.deviceModels[0]?.name} (
                                    {formatCurrency(selectedRequest?.deviceModels[0]?.deviceCount)})
                                </Text>
                            )}
                        {selectedRequest?.deviceModels && selectedRequest?.deviceModels.length > 1 && (
                            <span className="device-count-container">
                                <TooltipHost
                                    tooltipProps={tooltipProps}
                                    delay={TooltipDelay.zero}
                                    id={tooltipId}
                                    directionalHint={DirectionalHint.bottomCenter}
                                    styles={hostStyles}
                                    className="hostStyles-new">
                                    <Text className="device-count">+{selectedRequest?.deviceModels.length - 1}</Text>
                                </TooltipHost>
                            </span>
                        )}
                    </div>
                </div>
            </div>
            </DetailCard>

            <ContextualDetailsCard
                selectedRequest={selectedRequest}
                viewerRole={ViewerRole.Admin}
                isVisible={isNewTicketViewEnabled}
            />
            <GeneralFeedbackEmptyState isVisible={isNewTicketViewEnabled && isGeneralFeedback} />
            <FeedbackSourceCard
                selectedRequest={selectedRequest}
                viewerRole={ViewerRole.Admin}
                isVisible={isNewTicketViewEnabled}
            />

            <div className="tab-details-heading">TIME SENSITIVITY</div>
            <div className="tab-details-sub-heading">REQUESTED RESOLUTION DATE</div>
            <div className="tab-details-text">
                {isEditMode && (
                    <DatePicker
                        placeholder="From: MM/DD/YYYY"
                        ariaLabel="Select a date"
                        value={
                            moment(selectedRequest.requestedResolutionDate).isValid()
                            ? isEdittingRequestDetails
                                ? resolutionDateFieldValue
                                : new Date(ISOToDateTime(selectedRequest.requestedResolutionDate?.toString(), 'MMM DD YYYY'))
                            : null
                        }
                        onSelectDate={(value) => handleResolutionDate(value)}
                    />
                )}
                {!isEditMode
                    ? moment(selectedRequest.requestedResolutionDate?.toString()).isValid()
                        ? ISOToDateTime(selectedRequest.requestedResolutionDate?.toString(), 'MMM DD YYYY')
                        : '--'
                    : ''}
            </div>

            {filterField('whyThisDateIsImportant', selectedRequest.requestType) && (
                <div className="tab-details-sub-heading">WHY IS THIS DATE IMPORTANT?</div>
            )}
            {filterField('whyThisDateIsImportant', selectedRequest.requestType) && !isEditMode && (
                <div className="tab-details-text">
                    {whyThisDateIsImportant !== undefined && whyThisDateIsImportant && (
                        <span
                            dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(highlightTags(
                                    ReplaceUrlByAnchor(injectTargetToAnchor(whyThisDateIsImportant), 'link-item', true)
                                ))
                            }}></span>
                    )}
                    <Text>{!whyThisDateIsImportant && '--'}</Text>
                </div>
            )}
            {filterField('whyThisDateIsImportant', selectedRequest.requestType) && isEditMode && (
                <div className="desc-textfield-wrap m-b-20">
                    <RequestRichTextEditor
                        content={whyThisDateIsImportant}
                        hashTagsFormatted={hashTagsFormatted}
                        setHashTagsFormatted={setHashTagsFormatted}
                        setTextToSubmit={setTextToSubmit}
                        fileStorageContainer={BlobStorageContainer.COMMENT_STATUS_FILES}
                    />
                </div>
            )}

            {selectedRequest.requestType !== 1 && selectedRequest.requestType !== 3 && (
                <div className="tab-details-heading m-t-30">ADDITIONAL DETAILS :</div>
            )}
            {(getFieldValues(StringConstants.PFR_SUBTYPE) ||
                filterField(StringConstants.PRIORITY_NO, selectedRequest.requestType) ||
                getFieldValues(StringConstants.PRIORITY) ||
                getFieldValues(StringConstants.CRITICALITY) ||
                getFieldValues(StringConstants.IMPACT_REACH) ||
                getFieldValues(StringConstants.MAIN_REQUEST_TYPE) ||
                getFieldValues(StringConstants.SUB_REQUEST_TYPE) ||
                getFieldValues(StringConstants.TASK_STATUS)) && (
                <table className="dp-content-details-tbl">
                    <tbody>
                        <tr className="tbl-header">
                            {getFieldValues(StringConstants.PFR_SUBTYPE) && <td>REQUEST SUBTYPE</td>}
                            {filterField(StringConstants.PRIORITY_NO, selectedRequest.requestType) && <td>PRIORITY NO.</td>}
                            {getFieldValues(StringConstants.PRIORITY) && <td>PRIORITY</td>}
                            {getFieldValues(StringConstants.CRITICALITY) && <td>CRITICALITY</td>}

                            {getFieldValues(StringConstants.IMPACT_REACH) && <td>IMPACT REACH</td>}
                            {getFieldValues(StringConstants.MAIN_REQUEST_TYPE) && <td>MAIN REQUEST TYPE</td>}
                            {getFieldValues(StringConstants.SUB_REQUEST_TYPE) && <td>SUB-REQUEST TYPE</td>}
                            {getFieldValues(StringConstants.TASK_STATUS) && <td>TASK STATUS</td>}
                        </tr>
                        <tr>
                            {getFieldValues(StringConstants.PFR_SUBTYPE) && (
                                <td style={{ width: '200px' }}>
                                    {selectedRequest.isHidden ? (
                                        <Text>
                                            {getNameByID(getFieldValues(StringConstants.PFR_SUBTYPE) as EnumLookUp[], selectedRequest.pfrSubType)}
                                        </Text>
                                    ) : (
                                        <ActionDropdown
                                            data={getFieldValues(StringConstants.PFR_SUBTYPE) as EnumLookUp[]}
                                            onCompleteHandler={onCompleteHandler}
                                            fieldName="pfrSubtype"
                                            currentValue={selectedRequest.pfrSubType}
                                            isEditMode={isEditMode}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                </td>
                            )}
                            {filterField(StringConstants.PRIORITY_NO, selectedRequest.requestType) && (
                                <td style={{ width: '100px' }}>
                                    <Text>{selectedRequest.priorityNumber && selectedRequest.priorityNumber}</Text>
                                    <Text>{!selectedRequest.priorityNumber && '--'}</Text>
                                </td>
                            )}
                            {getFieldValues(StringConstants.PRIORITY) && (
                                <td style={{ width: '100px' }}>
                                    {selectedRequest.isHidden ? (
                                        <Text>
                                            {getNameByID(getFieldValues(StringConstants.PRIORITY) as EnumLookUp[], selectedRequest.priority)}
                                        </Text>
                                    ) : (
                                        <ActionDropdown
                                            data={getFieldValues(StringConstants.PRIORITY) as EnumLookUp[]}
                                            onCompleteHandler={onCompleteHandler}
                                            fieldName="priority"
                                            currentValue={selectedRequest.priority}
                                            isEditMode={isEditMode}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                </td>
                            )}
                            {getFieldValues(StringConstants.CRITICALITY) && (
                                <td style={{ width: '100px' }}>
                                    {selectedRequest.isHidden ? (
                                        <Text>
                                            {getNameByID(
                                                getFieldValues(StringConstants.CRITICALITY) as EnumLookUp[],
                                                selectedRequest.criticality
                                            )}
                                        </Text>
                                    ) : (
                                        <ActionDropdown
                                            data={getFieldValues(StringConstants.CRITICALITY) as EnumLookUp[]}
                                            onCompleteHandler={onCompleteHandler}
                                            fieldName="criticality"
                                            currentValue={selectedRequest.criticality}
                                            isEditMode={isEditMode}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                </td>
                            )}
                            {getFieldValues(StringConstants.IMPACT_REACH) && (
                                <td>
                                    {selectedRequest.isHidden ? (
                                        <Text>
                                            {getNameByID(
                                                getFieldValues(StringConstants.IMPACT_REACH) as EnumLookUp[],
                                                selectedRequest.impactReach
                                            )}
                                        </Text>
                                    ) : (
                                        <ActionDropdown
                                            data={getFieldValues(StringConstants.IMPACT_REACH) as EnumLookUp[]}
                                            onCompleteHandler={onCompleteHandler}
                                            fieldName="impactReach"
                                            currentValue={selectedRequest.impactReach}
                                            isEditMode={isEditMode}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                </td>
                            )}
                            {getFieldValues(StringConstants.MAIN_REQUEST_TYPE) && (
                                <td>
                                    {selectedRequest.isHidden ? (
                                        <Text>
                                            {getNameByID(
                                                getFieldValues(StringConstants.MAIN_REQUEST_TYPE) as EnumLookUp[],
                                                selectedRequest.mainRequestType
                                            )}
                                        </Text>
                                    ) : (
                                        <ActionDropdown
                                            data={getFieldValues(StringConstants.MAIN_REQUEST_TYPE) as EnumLookUp[]}
                                            onCompleteHandler={onCompleteHandler}
                                            fieldName="mainRequestType"
                                            currentValue={selectedRequest.mainRequestType}
                                            isEditMode={isEditMode}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                </td>
                            )}
                            {getFieldValues(StringConstants.SUB_REQUEST_TYPE) && (
                                <td>
                                    {selectedRequest.isHidden ? (
                                        <Text>
                                            {getNameByID(
                                                getFieldValues(StringConstants.SUB_REQUEST_TYPE) as EnumLookUp[],
                                                selectedRequest.subRequestType
                                            )}
                                        </Text>
                                    ) : (
                                        <ActionDropdown
                                            data={getFieldValues(StringConstants.SUB_REQUEST_TYPE) as EnumLookUp[]}
                                            onCompleteHandler={onCompleteHandler}
                                            fieldName="subRequestType"
                                            currentValue={selectedRequest.subRequestType}
                                            isEditMode={isEditMode}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                </td>
                            )}
                            {getFieldValues(StringConstants.TASK_STATUS) && !selectedRequest.isHidden && (
                                <td>
                                    <ActionDropdown
                                        data={getFieldValues(StringConstants.TASK_STATUS) as EnumLookUp[]}
                                        onCompleteHandler={onCompleteHandler}
                                        fieldName="taskStatus"
                                        currentValue={selectedRequest.taskStatus}
                                        isEditMode={isEditMode}
                                        selectedRequest={selectedRequest}
                                    />
                                </td>
                            )}
                            {getFieldValues(StringConstants.TASK_STATUS) && selectedRequest.isHidden && (
                                <td>{getNameByID(getFieldValues(StringConstants.TASK_STATUS) as EnumLookUp[], selectedRequest.taskStatus)}</td>
                            )}
                        </tr>
                    </tbody>
                </table>
            )}

            {getFieldValues(StringConstants.BUG_INFORMATION) && (
                <table className="dp-content-details-tbl">
                    <tbody>
                        <tr className="tbl-header">
                            {getFieldValues(StringConstants.BUG_INFORMATION) && (
                                <td>
                                    <span className="m-r-10">
                                        {`${
                                            selectedRequest.requestType === RequestType.CssReviewRequest ||
                                            selectedRequest.requestType === RequestType.CXP_TechnicalRequest
                                                ? 'BUG INFORMATION'
                                                : 'Azure DevOps Link '
                                        }`}
                                    </span>
                                    {!selectedRequest.isHidden && (
                                        <Link className="add-bug-link" onClick={() => setIsBugInfoModalOpen(true)}>
                                            {selectedRequest.requestType === 9 ? "+Add link" : "+Add bug"}
                                        </Link>
                                    )}
                                </td>
                            )}
                        </tr>
                        <tr>
                            {getFieldValues(StringConstants.BUG_INFORMATION) && (
                                <td>
                                    {(!selectedRequest.bugInformations || selectedRequest.bugInformations?.length === 0) && '--'}
                                    {selectedRequest.bugInformations &&
                                        selectedRequest.bugInformations.map((item) => {
                                            //pairings of valid RequestType & BugInformation #s
                                            const validPairings: Record<number, number[]> = {
                                                [RequestType.CssReviewRequest]: [BugInformation.PLE_Bug, BugInformation.MSFT_Devices_Non_PLE_Bug, BugInformation.OS_Bug, BugInformation.Hub_OS_Bug, BugInformation.Other_Bugs],
                                                [RequestType.CXP_PFR_Request]: [BugInformation.Azure_Devops_Link],
                                                [RequestType.CXP_TechnicalRequest]: [BugInformation.PLE_Bug, BugInformation.MSFT_Devices_Non_PLE_Bug, BugInformation.OS_Bug, BugInformation.Hub_OS_Bug, BugInformation.Other_Bugs],
                                            }

                                            if(validPairings[selectedRequest.requestType]?.includes(item.bugInformation)) {
                                                return (
                                                <div className="bug-info-disp-item">
                                                    {GetText(getFieldValues(StringConstants.BUG_INFORMATION) as EnumLookUp[], item.bugInformation)} -{' '}
                                                    {isValidURL(item?.url) && (
                                                        <span
                                                            dangerouslySetInnerHTML={{
                                                                __html: DOMPurify.sanitize(convertToURL(
                                                                    item?.url?.replaceAll(',', ' ,'),
                                                                    'link-item',
                                                                    true
                                                                ))
                                                            }}
                                                        ></span>
                                                    )}
                                                    {!isValidURL(item?.url) && <Text>{item?.url}</Text>}
                                                </div>
                                                );
                                            }
                                            return null;
                                        }
                                    )}
                                </td>
                            )}
                        </tr>
                    </tbody>
                </table>
            )}
            {filterField(StringConstants.BUISNESS_PROBLEM, selectedRequest.requestType) && (
                <div className="tab-details-sub-heading">WHAT BUSINESS PROBLEM IS SOLVED</div>
            )}
            {filterField(StringConstants.BUISNESS_PROBLEM, selectedRequest.requestType) && !isEditMode && (
                <div className="dp-content-text">
                    <Text>{selectedRequest.whatBusinessProblemIsSolved && selectedRequest.whatBusinessProblemIsSolved}</Text>
                    <Text>{!selectedRequest.whatBusinessProblemIsSolved && '--'}</Text>
                </div>
            )}
            {filterField(StringConstants.BUISNESS_PROBLEM, selectedRequest.requestType) && isEditMode && (
                <div className="desc-textfield-wrap m-b-20">
                    <TextField
                        value={isEdittingRequestDetails ? businessProblemFieldValue : whatbusinessproblemissolved}
                        placeholder="What business problem is solved"
                        multiline
                        resizable={false}
                        borderless
                        rows={8}
                        onChange={handleWhatbusinessproblemissolvedChange}
                    />
                </div>
            )}

            {filterField(StringConstants.COMPETITIVE_OFFERING, selectedRequest.requestType) && (
                <div className="tab-details-sub-heading">COMPETITIVE OFFERING</div>
            )}
            {filterField(StringConstants.COMPETITIVE_OFFERING, selectedRequest.requestType) && !isEditMode && (
                <div className="dp-content-text">
                    <Text>{selectedRequest.competitiveOffering && selectedRequest.competitiveOffering}</Text>
                    <Text>{!selectedRequest.competitiveOffering && '--'}</Text>
                </div>
            )}
            {filterField(StringConstants.COMPETITIVE_OFFERING, selectedRequest.requestType) && isEditMode && (
                <div className="desc-textfield-wrap m-b-20">
                    <TextField
                        value={isEdittingRequestDetails ? compOfferFieldValue : competitiveoffering}
                        placeholder="Competitive offering"
                        multiline
                        resizable={false}
                        borderless
                        rows={8}
                        onChange={handleCompetitiveofferingChange}
                    />
                </div>
            )}

            {filterField(StringConstants.IS_DESIGNED_FOR_SURFACE_POSSIBLE, selectedRequest.requestType) && (
                <div className="tab-details-sub-heading">IS DFS (DESIGNED FOR SURFACE) POSSIBLE?</div>
            )}
            {filterField(StringConstants.IS_DESIGNED_FOR_SURFACE_POSSIBLE, selectedRequest.requestType) && !isEditMode && (
                <div className="dp-content-text">
                    <Text>{selectedRequest.isDesignForSurfacedPossible && selectedRequest.isDesignForSurfacedPossible}</Text>
                    <Text>{!selectedRequest.isDesignForSurfacedPossible && '--'}</Text>
                </div>
            )}
            {filterField(StringConstants.IS_DESIGNED_FOR_SURFACE_POSSIBLE, selectedRequest.requestType) && isEditMode && (
                <div className="desc-textfield-wrap m-b-20">
                    <TextField
                        value={isEdittingRequestDetails ? isDfsFieldValue : isdesignforsurfacedfspossible}
                        placeholder="Is DFS (Designed for Surface) possible?"
                        multiline
                        resizable={false}
                        borderless
                        rows={8}
                        onChange={handleIsdesignforsurfacedfspossibleChange}
                    />
                </div>
            )}

            {filterField(StringConstants.IS_DCF_REQUEST, selectedRequest.requestType) && (
                <>
                    <div className="tab-details-sub-heading">IS THIS A DCF REQUEST?</div>
                    <div className="dp-content-text">
                        {selectedRequest.isHidden ? (
                            <Text>{getNameByID(yesNoLookup, selectedRequest.isDcfRequest)}</Text>
                        ) : (
                            <ActionDropdown
                                data={yesNoLookup}
                                onCompleteHandler={onCompleteHandler}
                                fieldName={StringConstants.IS_DCF_REQUEST}
                                currentValue={selectedRequest.isDcfRequest}
                                isEditMode={isEditMode}
                                selectedRequest={selectedRequest}
                            />
                        )}
                    </div>
                </>
            )}

            {selectedRequest.isDcfRequest && filterField('dcfJustification', selectedRequest.requestType) && (
                <div className="tab-details-sub-heading">DCF JUSTIFICATION</div>
            )}
            {selectedRequest.isDcfRequest && filterField('dcfJustification', selectedRequest.requestType) && !isEditMode && (
                <div className="dp-content-text">
                    <Text>{selectedRequest.dcfJustification && selectedRequest.dcfJustification}</Text>
                    <Text>{!selectedRequest.dcfJustification && '--'}</Text>
                </div>
            )}
            {selectedRequest.isDcfRequest && filterField('dcfJustification', selectedRequest.requestType) && isEditMode && (
                <div className="desc-textfield-wrap m-b-20">
                    <TextField
                        value={isEdittingRequestDetails ? dcfJustificationFieldValue : dcfJustification}
                        placeholder="DCF justification"
                        multiline
                        resizable={false}
                        borderless
                        rows={8}
                        onChange={handleDcfJustificationChange}
                    />
                </div>
            )}
            {selectedRequest.isDcfRequest && filterField(StringConstants.IS_DCF_REQUEST, selectedRequest.requestType) && (
                <table className="dp-content-details-tbl">
                    <tbody>
                        <tr className="tbl-header">
                            <td>IS THIS A BLOCKING SALE?</td>
                            <td>DCF APPROVED?</td>
                            <td>DCF APPROVED VALUE</td>
                            <td>DCF SR's</td>
                        </tr>
                        <tr>
                            <td>
                                {selectedRequest.isHidden ? (
                                    <Text>{getNameByID(yesNoLookup, selectedRequest.isBlockingSale)}</Text>
                                ) : (
                                    <ActionDropdown
                                        data={yesNoLookup}
                                        onCompleteHandler={onCompleteHandler}
                                        fieldName="isBlockingSale"
                                        currentValue={selectedRequest.isBlockingSale}
                                        isEditMode={isEditMode}
                                        selectedRequest={selectedRequest}
                                    />
                                )}
                            </td>
                            <td>
                                {selectedRequest.isHidden ? (
                                    <Text>{getNameByID(yesNoLookup, selectedRequest.isDcfApproved)}</Text>
                                ) : (
                                    <ActionDropdown
                                        data={yesNoLookup}
                                        onCompleteHandler={onCompleteHandler}
                                        fieldName="isDcfApproved"
                                        currentValue={selectedRequest.isDcfApproved}
                                        isEditMode={isEditMode}
                                        selectedRequest={selectedRequest}
                                    />
                                )}
                            </td>
                            <td>
                                {!isEditMode && (
                                    <>
                                        {selectedRequest.dcfApprovedValue && (
                                            <Text>${formatCurrency(selectedRequest.dcfApprovedValue)}</Text>
                                        )}
                                        <Text>{!selectedRequest.dcfApprovedValue && '--'}</Text>
                                    </>
                                )}
                                {isEditMode && (
                                    <div className="desc-textfield-wrap m-b-20">
                                        <NumberFormat
                                            thousandSeparator={true}
                                            value={isEdittingRequestDetails ? dcfApprovedFieldValue : dcfApprovedValue}
                                            onValueChange={handleDcfApprovedValueChange}
                                            placeholder="eg: $167,700"
                                            allowNegative={false}
                                            prefix={'$'}
                                            decimalScale={2}
                                            className={`format-field regular-text-field   `}
                                            inputMode="numeric"
                                        />
                                    </div>
                                )}
                            </td>
                            <td>
                                {!isEditMode && (
                                    <>
                                        <Text>{selectedRequest.dcfSR && selectedRequest.dcfSR}</Text>
                                        <Text>{!selectedRequest.dcfSR && '--'}</Text>
                                    </>
                                )}
                                {isEditMode && (
                                    <div className="desc-textfield-wrap m-b-20">
                                        <TextField
                                            value={isEdittingRequestDetails ? dcfSRFieldValue : dcfSR}
                                            placeholder="DCF SR's"
                                            resizable={false}
                                            borderless
                                            onChange={handleDcfSRChange}
                                        />
                                    </div>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            )}
            {(filterField(StringConstants.PROOF_OF_PURCHASE_ATTACHED, selectedRequest.requestType) ||
                filterField(StringConstants.SERIAL_NUMBERS_ATTACHED, selectedRequest.requestType) ||
                filterField(StringConstants.CSS_CASE_NUMBER, selectedRequest.requestType)) && (
                <table className="dp-content-details-tbl">
                    <tbody>
                        <tr className="tbl-header">
                            {filterField(StringConstants.PROOF_OF_PURCHASE_ATTACHED, selectedRequest.requestType) && (
                                <td>PROOF OF PURCHASE ATTACHED?</td>
                            )}
                            {filterField(StringConstants.SERIAL_NUMBERS_ATTACHED, selectedRequest.requestType) && (
                                <td>SERIAL NUMBER ATTACHED?</td>
                            )}
                            {filterField(StringConstants.CSS_CASE_NUMBER, selectedRequest.requestType) && <td>CSS CASE NUMBER</td>}
                        </tr>
                        <tr>
                            {filterField(StringConstants.PROOF_OF_PURCHASE_ATTACHED, selectedRequest.requestType) && (
                                <td>
                                    {selectedRequest.isHidden ? (
                                        <Text>{getNameByID(yesNoLookup, selectedRequest.proofOfPurchaseAttached)}</Text>
                                    ) : (
                                        <ActionDropdown
                                            data={yesNoLookup}
                                            onCompleteHandler={onCompleteHandler}
                                            fieldName="proofOfPurchaseAttached"
                                            currentValue={selectedRequest.proofOfPurchaseAttached}
                                            isEditMode={isEditMode}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                </td>
                            )}
                            {filterField(StringConstants.SERIAL_NUMBERS_ATTACHED, selectedRequest.requestType) && (
                                <td>
                                    {selectedRequest.isHidden ? (
                                        <Text>{getNameByID(yesNoLookup, selectedRequest.serialNumbersAttached)}</Text>
                                    ) : (
                                        <ActionDropdown
                                            data={yesNoLookup}
                                            onCompleteHandler={onCompleteHandler}
                                            fieldName="serialNumbersAttached"
                                            currentValue={selectedRequest.serialNumbersAttached}
                                            isEditMode={isEditMode}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                </td>
                            )}
                            {filterField(StringConstants.CSS_CASE_NUMBER, selectedRequest.requestType) && (
                                <td>
                                    {!isEditMode && (
                                        <>
                                            <Text className="tbl-css-num">
                                                {selectedRequest.cssCaseNumber && selectedRequest.cssCaseNumber}
                                            </Text>
                                            <Text>{!selectedRequest.cssCaseNumber && '--'}</Text>
                                        </>
                                    )}
                                    {isEditMode && (
                                        <div className="desc-textfield-wrap m-b-20">
                                            <TextField
                                                value={isEdittingRequestDetails ? cssCaseNumberFieldValue : cssCaseNumber}
                                                placeholder="CSS case number"
                                                resizable={false}
                                                borderless
                                                onChange={handleCssCaseNumberChange}
                                            />
                                        </div>
                                    )}
                                </td>
                            )}
                        </tr>
                    </tbody>
                </table>
            )}

            {filterField(StringConstants.ANSWER, selectedRequest.requestType) && <div className="tab-details-sub-heading">ANSWER</div>}
            {filterField(StringConstants.ANSWER, selectedRequest.requestType) && !isEditMode && (
                <div className="dp-content-text">
                    <Text>{selectedRequest.answer && selectedRequest.answer}</Text>
                    <Text>{!selectedRequest.answer && '--'}</Text>
                </div>
            )}
            {filterField(StringConstants.ANSWER, selectedRequest.requestType) && isEditMode && (
                <div className="desc-textfield-wrap m-b-20">
                    <TextField
                        value={isEdittingRequestDetails ? answerFieldValue : answer}
                        placeholder="Answer"
                        multiline
                        resizable={false}
                        borderless
                        rows={8}
                        onChange={handleAnswerChange}
                    />
                </div>
            )}
            <BugInformationModal
                modalState={isBugInfoModalOpen}
                updateModalState={setIsBugInfoModalOpen}
                selectedRequest={selectedRequest}
                onUpdateHandler={bugInfoUpdateHandler}
            />
        </div>
    );
};

export default RequestDetailsTab;
